/**
 * The largest request body we will attempt to send.
 *
 * Vercel rejects a request body over roughly 4.5 MB with
 * 413 FUNCTION_PAYLOAD_TOO_LARGE, and it does so at the edge — before the OCR
 * route runs, so that route's own MAX_FILE_SIZE of 10 MB never gets a say. A
 * 6 MB one-page PDF therefore passes every check this app makes and dies at the
 * platform, arriving back as three failed attempts and an empty result, which
 * the caller then reports as text that could not be recognised.
 *
 * Where the number comes from: measured against production /api/ocr on
 * 2026-08-21. A padded 4.0 MB PDF returned 200 in 7.8 s; 5.0 MB, 6 MB, 9 MB and
 * 11 MB all returned 413 in under 1.5 s. So 4 MB is the largest size proven to
 * work, and the remaining gap up to ~4.5 MB absorbs the multipart/form-data
 * overhead the request carries beside the file — promptProfile,
 * preprocessProfile, attempt, docId, pageIndex, and the boundary markers.
 *
 * Deliberately set below the platform figure rather than at it. That figure is
 * documented as approximate, and guessing high costs a request that cannot
 * succeed, while guessing low costs a rendered page that works.
 */
export const MAX_UPLOAD_BYTES = 4 * 1024 * 1024;

export type OnePagePdfOcrPlan = 'pdf' | 'rendered' | 'too-large';

/**
 * Decides how a genuinely one-page PDF should be sent for OCR.
 *
 * The PDF itself wins whenever it fits, because it carries whatever text layer
 * the file already has and OCR of a rendered image cannot recover that.
 *
 * Over the limit the rendered page is a real second chance rather than a
 * consolation prize: it is a JPEG rasterised at PDF_RENDER_SCALE, so a PDF that
 * is large because of embedded fonts or a high-resolution scan usually renders a
 * great deal smaller. It is not guaranteed to, which is why its size is checked
 * against the same ceiling instead of being assumed to fit.
 *
 * renderedBytes is null when the page has not been rendered yet, and also when
 * rendering produced nothing — both mean the fallback is unavailable, and both
 * should end in an honest refusal rather than a doomed upload.
 */
export function planOnePagePdfOcr(
  pdfBytes: number,
  renderedBytes: number | null,
): OnePagePdfOcrPlan {
  if (pdfBytes <= MAX_UPLOAD_BYTES) return 'pdf';
  if (renderedBytes !== null && renderedBytes <= MAX_UPLOAD_BYTES) return 'rendered';
  return 'too-large';
}

/** Formats a byte count the way the Polish UI writes numbers: "4,8 MB". */
export function formatMegabytes(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}

/**
 * Quality used whenever this app re-encodes a bitmap it is going to upload.
 *
 * The alternative was PNG, which is what three call sites used to produce, and
 * PNG is the wrong format for a photographed page: it is lossless, so a
 * twelve-megapixel photo re-encodes to something several times larger than the
 * JPEG it came from, on a path with a hard 4 MB ceiling and a user who may be on
 * mobile data. 0.92 is high enough that the artefacts are well below what the
 * server's own downscale to 2000 px discards anyway.
 */
export const REENCODE_JPEG_QUALITY = 0.92;

/** The MIME type those re-encodes produce, named so the label cannot drift from the bytes. */
export const REENCODE_MIME = 'image/jpeg';

/**
 * Whether an image is small enough to attempt at all.
 *
 * The image path had no client-side check of any kind: an oversized upload went
 * to the platform, came back 413 before our route ran, and reached the user as
 * text that could not be recognised - a description of a document that was never
 * read. Same ceiling as the PDF path, same reasoning, and deliberately the same
 * constant so the two cannot drift.
 */
export function isWithinUploadLimit(bytes: number): boolean {
  return bytes <= MAX_UPLOAD_BYTES;
}
